// L04 · Type It Again (SPEC §3/§6, Act II). First inverted biometric.
// "Type your name." → "Type it again. Exactly the same." Inter-key intervals are recorded
// locally for both attempts and compared. A human's rhythm varies — that is the pass. A
// paste, or a machine-perfect match (correlation > 0.985 AND mean-diff < 8ms), is a strike.
//
// Nothing about the timing leaves the device; only the verdict does. Desktop parity:
// identical (§3). No traits (§3).

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import { KeystrokeRecorder, compareTimings, MIN_INTERVALS } from "@/engine/biometrics.ts";
import { L04 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

export const L04: Level = {
  id: "L04",
  act: 2,
  title: "Type It Again",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      const prompt = promptEl(V.prompt1);

      const input = el("input", "field");
      input.type = "text";
      input.autocomplete = "off";
      input.spellcheck = false;
      input.setAttribute("autocapitalize", "off");
      input.setAttribute("autocorrect", "off");
      input.setAttribute("aria-label", "Your name");

      const button = el("button", "btn");
      button.textContent = V.verify;

      host.append(prompt, input, button);
      // Autofocus so the keystroke recording starts cleanly on the first key.
      window.setTimeout(() => input.focus(), 0);

      const recorder = new KeystrokeRecorder(input);
      let phase: 1 | 2 = 1;
      let first: { intervals: number[]; pasted: boolean } | null = null;

      const submit = () => {
        if (!input.value.trim()) return; // need something to measure

        if (phase === 1) {
          first = { intervals: recorder.intervals(), pasted: recorder.pasted };
          phase = 2;
          recorder.reset();
          input.value = "";
          prompt.textContent = V.prompt2;
          button.textContent = V.again;
          window.setTimeout(() => input.focus(), 0);
          return;
        }

        // phase 2 — decide.
        const second = { intervals: recorder.intervals(), pasted: recorder.pasted };
        recorder.detach();

        if (first!.pasted || second.pasted) {
          done({ pass: true, strike: true, line: V.pasted });
          return;
        }
        const a = first!.intervals;
        const b = second.intervals;
        if (a.length < MIN_INTERVALS || b.length < MIN_INTERVALS) {
          done({ pass: true, line: V.tooShort });
          return;
        }
        const cmp = compareTimings(a, b);
        if (cmp.tooIdentical) {
          done({ pass: true, strike: true, line: V.identical });
        } else {
          done({ pass: true, line: V.natural });
        }
      };

      button.addEventListener("click", submit);
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          submit();
        }
      });

      return () => recorder.detach();
    });
  },
};
